/**
 * HTML Validation utilities for Webinar Mailing Editor
 * Validates email content for proper HTML structure and link rules
 */

import { validatePlaceholders, WEBINAR_PLACEHOLDERS } from '../../helpers/placeholderValidation';

export interface ValidationResult {
  valid: boolean;
  errors: string[]; // Blocking issues - prevent save
  warnings: string[]; // Non-blocking - allow save with warning
}

/**
 * Validates email HTML content
 * Rules:
 * - Must contain {WEBINAR_LINK} placeholder
 * - No other links allowed (no mailto:, http://, https://, anchors)
 * - HTML must be well-formed (no unclosed tags)
 */
export function validateEmailHtml(html: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check for empty content
  if (!html || html.trim() === '' || html.trim() === '<p></p>' || html.trim() === '<p><br></p>') {
    errors.push('E-Mail-Inhalt darf nicht leer sein');
    return { valid: false, errors, warnings };
  }

  errors.push(
    ...validatePlaceholders(html, '', {
      allowed: WEBINAR_PLACEHOLDERS,
      required: 'WEBINAR_LINK',
      system: 'webinar',
    }),
  );

  // Parse HTML and check for malformed structure
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Check for parser errors
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    errors.push('HTML-Struktur ist fehlerhaft: ' + parserError.textContent?.substring(0, 100));
  }

  // Extract and validate all links
  const links = doc.querySelectorAll('a[href]');
  const invalidLinks: string[] = [];

  links.forEach((link) => {
    const href = link.getAttribute('href') || '';

    // Allow {WEBINAR_LINK} placeholder
    if (href === '{WEBINAR_LINK}' || href.includes('{WEBINAR_LINK}')) {
      return; // Valid
    }

    // All other links are invalid
    if (href.trim() !== '') {
      invalidLinks.push(href);
    }
  });

  if (invalidLinks.length > 0) {
    invalidLinks.forEach((link) => {
      // Truncate long links for display
      const displayLink = link.length > 50 ? link.substring(0, 47) + '...' : link;
      errors.push(`Unerlaubter Link gefunden: ${displayLink}`);
    });
  }

  // Check for common HTML issues using regex (backup validation)
  const htmlIssues = checkHtmlStructure(html);
  htmlIssues.forEach((issue) => errors.push(issue));

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validates email subject line
 * Returns warnings (not errors) for length issues
 */
export function validateSubject(subject: string): string[] {
  const warnings: string[] = [];

  if (!subject || subject.trim() === '') {
    return ['Betreff darf nicht leer sein'];
  }

  if (subject.length > 60) {
    warnings.push(
      `Betreff ist ${subject.length} Zeichen lang - wird auf Mobilgeräten abgeschnitten (max. 60 empfohlen)`,
    );
  }

  return warnings;
}

/**
 * Combined validation for full email (subject + body)
 */
export function validateEmail(subject: string, htmlBody: string): ValidationResult {
  const bodyValidation = validateEmailHtml(htmlBody);
  const subjectWarnings = validateSubject(subject);

  // Subject empty is an error, not a warning
  const subjectErrors: string[] = [];
  const subjectWarningsFiltered: string[] = [];

  subjectWarnings.forEach((w) => {
    if (w.includes('darf nicht leer sein')) {
      subjectErrors.push(w);
    } else {
      subjectWarningsFiltered.push(w);
    }
  });

  return {
    valid: bodyValidation.valid && subjectErrors.length === 0,
    errors: [...subjectErrors, ...bodyValidation.errors],
    warnings: [...subjectWarningsFiltered, ...bodyValidation.warnings],
  };
}

/**
 * Check for common HTML structure issues
 * Uses regex patterns as backup validation
 */
function checkHtmlStructure(html: string): string[] {
  const issues: string[] = [];

  // Check for unclosed common tags
  const tagsToCheck = [
    'div',
    'p',
    'span',
    'table',
    'tr',
    'td',
    'th',
    'ul',
    'ol',
    'li',
    'a',
    'strong',
    'em',
    'b',
    'i',
  ];

  for (const tag of tagsToCheck) {
    const openPattern = new RegExp(`<${tag}(?:\\s[^>]*)?>`, 'gi');
    const closePattern = new RegExp(`</${tag}>`, 'gi');
    const selfClosePattern = new RegExp(`<${tag}[^>]*/\\s*>`, 'gi');

    const openMatches = html.match(openPattern) || [];
    const closeMatches = html.match(closePattern) || [];
    const selfCloseMatches = html.match(selfClosePattern) || [];

    // Subtract self-closing from open count
    const openCount = openMatches.length - selfCloseMatches.length;
    const closeCount = closeMatches.length;

    if (openCount > closeCount) {
      issues.push(`HTML-Fehler: ${openCount - closeCount} nicht geschlossene <${tag}> Tag(s)`);
    } else if (closeCount > openCount) {
      issues.push(`HTML-Fehler: ${closeCount - openCount} unerwartete </${tag}> Tag(s)`);
    }
  }

  // Check for unclosed img/br/hr (should be self-closing or empty)
  // These are void elements and don't need closing tags - no check needed

  return issues;
}

/**
 * Available placeholders for the email editor
 */
export const PLACEHOLDERS = [
  // Core placeholders
  {
    placeholder: '{WEBINAR_LINK}',
    label: 'Anmelde-Link',
    description: 'Link zur Webinar-Registrierung',
    required: true,
  },
  {
    placeholder: '{WEBINAR_TITLE}',
    label: 'Webinar-Titel',
    description: 'öffentlicher Titel des Webinars',
    required: false,
  },
  {
    placeholder: '{EMAIL}',
    label: 'E-Mail',
    description: 'E-Mail-Adresse des Empfängers',
    required: false,
  },
  // Dynamic date placeholders
  {
    placeholder: '{WEBINAR_DATE_DYNAMIC}',
    label: 'Datum dynamisch',
    description: 'z.B. "Morgen um 11:00" oder "am Samstag um 11:00"',
    required: false,
  },
  {
    placeholder: '{WEBINAR_DATE_DYNAMIC_UPPER}',
    label: 'Datum dynamisch (GROSS)',
    description: 'z.B. "MORGEN UM 11:00"',
    required: false,
  },
  // Static date placeholders
  {
    placeholder: '{WEBINAR_DAY_WRITTEN}',
    label: 'Wochentag',
    description: 'Webinartag ausgeschrieben (z.B. Samstag)',
    required: false,
  },
  {
    placeholder: '{WEBINAR_DATE:d.}',
    label: 'Tag',
    description: 'Webinartag als Zahl (z.B. 01.)',
    required: false,
  },
  {
    placeholder: '{WEBINAR_DATE:d.m.}',
    label: 'Tag.Monat',
    description: 'Datum im Format 01.01.',
    required: false,
  },
  {
    placeholder: '{WEBINAR_DATE:d.m.Y}',
    label: 'Volles Datum',
    description: 'Datum im Format 01.01.2025',
    required: false,
  },
  {
    placeholder: '{WEBINAR_DATE:Y}',
    label: 'Jahr',
    description: 'Webinar-Jahr (z.B. 2025)',
    required: false,
  },
  {
    placeholder: '{WEBINAR_MONAT}',
    label: 'Monat',
    description: 'Monat ausgeschrieben (z.B. März)',
    required: false,
  },
  {
    placeholder: '{WEBINAR_TIME}',
    label: 'Uhrzeit',
    description: 'Webinar-Uhrzeit (z.B. 11:00)',
    required: false,
  },
];
